//@flow
import React, {useMemo, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useHistory, useParams} from 'react-router-dom';

import {
  Grid,
  Hidden,
  makeStyles,
  Divider,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import {
  ArrowBack,
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@material-ui/icons';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomCategoryChip from './ColorsPalette/CustomCategoryChip';
import {colors} from '../../../assets/styles/colors';
import wsspIcon from '../../../assets/img/WhatsApp.png';

import CollapseItem from './CollapseItem/CollapseItem';

import {generateWhatsappLink, replaceTemplate} from '../../../utils/share';
import {usePage} from '../../page/utils';

import {commaFormat} from '../../../utils/number';
import {getCustomerFirstName} from '../utils';
import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import {useIsFeatureInTier} from '../../../utils/component';
import {ORDER_SIZE} from '../duck';

import type {
  Store,
  User,
  Currency,
  CrmCustomer,
  Pagination,
  Order,
} from '../../../entities';

type Props = {
  currency?: Currency,
  message?: string,
  user?: User,
  store?: Store,
  customer?: CrmCustomer,
  orderPagination?: Pagination,
  isFetching?: boolean,
  isFetchingOrders?: boolean,
  orders?: Order[],
  fetchCustomer: ({customerId: string}) => void,
  resetCustomer: () => void,
  fetchCustomerMessage: () => void,
  fetchCustomerOrders: ({customerId: string}) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#f8f9fb',
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 0, 2),
    },
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  whiteCard: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    // height: '100%',
  },
  gridContent: {
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 4, 0, 4),
    marginBottom: 64,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 180,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 0, 2),
      marginBottom: 220,
    },
  },
  rowContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  textGray: {
    color: colors.grey800,
    fontSize: '14px',
  },
  singleCard: {
    width: '100%',
    background: '#FCFCFD',
    border: '1px solid #dedee4',
    borderRadius: '8px',
    padding: theme.spacing(2),
  },
  singleCardContent: {
    margin: theme.spacing(2, 0),
  },
  singleCardText: {
    color: colors.grey900,
    fontSize: '14px',
    fontWeight: 600,
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  titleSection: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '16px',
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnExpand: {
    color: colors.grey800,
    fontSize: '14px',
  },
  circleAvatar: {
    backgroundColor: '#F4F4F6',
    minWidth: '3.5rem',
    minHeight: '3.5rem',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  shortName: {
    color: colors.grey900,
    fontSize: '24px',
    fontWeight: 600,
  },
  wrapChip: {
    margin: theme.spacing(1, 0),
  },
  headerCustomerInfo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  avatarWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bigTextBold: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.grey900,
    textAlign: 'center',
  },
  totalPurchase: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  totalAmmount: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start',
    },
  },
  wrapRate: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  wrapBtnWssp: {
    display: 'flex',
    justifyContent: 'center',
  },
  btnWssp: {
    color: '#00B282',
    fontSize: '14px',
    '& img': {
      marginRight: theme.spacing(1.5),
    },
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  btnShowMore: {
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  ordersLoader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.spacing(1, 0),
  },
}));

export default usePage<Props>(Customer, 'Customer');

function Customer(props: Props) {
  const {
    store,
    message,
    currency,
    customer,
    orderPagination,
    orders,
    isFetching,
    isFetchingOrders,
    fetchCustomer,
    fetchCustomerMessage,
    fetchCustomerOrders,
    resetCustomer,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const {customerId} = useParams();

  const hasCustomerFeature = useIsFeatureInTier('CRM_CUSTOMER');
  const [collapseInfo, setCollapseInfo] = useState(true);

  useEffect(() => {
    if (hasCustomerFeature && customerId) {
      fetchCustomer({customerId});
    }
  }, [customerId, fetchCustomer, hasCustomerFeature]);

  useEffect(() => {
    if (hasCustomerFeature) {
      fetchCustomerMessage();
    } else {
      history.push('/customers');
    }
  }, [fetchCustomerMessage, hasCustomerFeature, history]);

  useEffect(() => {
    return () => resetCustomer();
  }, [resetCustomer]);

  const shortName = useMemo(() => {
    if (!customer) return '';
    return customer.fullName
      ? customer.fullName
          .split(' ')
          .slice(0, 2)
          .map(n => (n[0] ? n[0].toUpperCase() : ''))
          .join('')
      : '';
  }, [customer]);

  const wsspMessage = useMemo(() => {
    if (!customer || !store) return '';
    let messageToSend = '';
    if (message) {
      messageToSend = replaceTemplate(message, {
        nombre_tienda: store ? store.name : '',
        nombre_cliente: getCustomerFirstName(customer),
        link_tienda:
          store && store.domains && store.domains[0].domainName
            ? store.domains[0].domainName
            : '',
      });
    }
    return generateWhatsappLink(customer.phone, messageToSend);
  }, [customer, message, store]);

  const canAddPage = useMemo(() => {
    return (
      orders &&
      orderPagination &&
      orderPagination.total > ORDER_SIZE &&
      orders.length < orderPagination.total
    );
  }, [orderPagination, orders]);

  const handleFetchMoreOrders = () => {
    if (!isFetching && canAddPage) {
      if (customerId) fetchCustomerOrders({customerId});
    }
  };

  const personalInfoCustomer = (
    <div className={classes.whiteCard}>
      <div className={classes.headerCustomerInfo}>
        <div className={classes.avatarWrap}>
          <div className={classes.circleAvatar}>
            <Typography className={classes.shortName}>{shortName}</Typography>
          </div>
          <Typography className={classes.bigTextBold}>
            {customer ? customer.fullName : ''}
          </Typography>
          <div className={classes.wrapChip}>
            <CustomCategoryChip
              thin
              category={
                customer && customer.crmCustomerCategory
                  ? customer.crmCustomerCategory
                  : undefined
              }
            />
          </div>
        </div>
      </div>
      <Collapse in={collapseInfo}>
        <div className={classes.cardContent}>
          <div className={classes.rowContent}>
            <div className={classes.singleCard}>
              <Typography className={classes.singleCardText}>
                Información de Cliente
              </Typography>
              <Divider className={classes.divider} />
              <div className={classes.singleCardContent}>
                <Typography className={classes.textGray}>
                  DNI: {customer ? customer.identificationNumber : ''}
                </Typography>
                <Typography className={classes.textGray}>
                  Teléfono: {customer ? customer.phone : ''}
                </Typography>
              </div>
              <Divider className={classes.divider} />
              <div className={classes.wrapBtnWssp}>
                <CustomButton
                  className={classes.btnWssp}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={wsspMessage}
                >
                  <img src={wsspIcon} alt="contact wssp" />
                  Contactar
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.cardContent}>
          <div className={classes.rowContent}>
            <div className={classes.singleCard}>
              <Typography className={classes.singleCardText}>
                Dirección de envío
              </Typography>
              <Divider className={classes.divider} />
              <div className={classes.singleCardContent}>
                <Typography className={classes.textGray}>
                  {customer ? customer.fullAddress : ''}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
      <Hidden mdUp>
        <div className={classes.collapseBtn}>
          <CustomButton
            onClick={() => setCollapseInfo(!collapseInfo)}
            className={classes.btnExpand}
          >
            {`Ver ${collapseInfo ? 'menos' : 'más'} información`}
            {collapseInfo ? <ExpandLessRounded /> : <ExpandMoreRounded />}
          </CustomButton>
        </div>
      </Hidden>
    </div>
  );

  return (
    <div className={classes.root}>
      {isFetching && <LoadingSpinner />}
      <div className={classes.headerWrapper}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => history.push('/customers')}
          >
            <ArrowBack />
          </IconButton>
          Detalle de cliente
        </Typography>
      </div>
      {!isFetching && customer ? (
        <div className={classes.content}>
          <Hidden mdUp>
            <Grid container spacing={2} className={classes.gridContent}>
              <Grid item xs={12}>
                {personalInfoCustomer}
              </Grid>
              <Grid item xs={12}>
                <div className={classes.whiteCard}>
                  <div className={classes.cardContent}>
                    <div className={classes.totalPurchase}>
                      <div className={classes.totalAmmount}>
                        <Typography className={classes.titleSection}>
                          Monto total de compra
                        </Typography>
                        {/* <Typography className={classes.textGray}>
                          Año: 2021
                        </Typography> */}
                      </div>
                      <Divider className={classes.divider} />
                      <Typography className={classes.bigTextBold}>
                        {commaFormat(
                          customer && customer.totalOrderAmount
                            ? customer.totalOrderAmount
                            : '',
                          currency ? currency.symbol : ''
                        )}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className={classes.whiteCard}>
                  <div className={classes.cardContent}>
                    <div>
                      <Typography className={classes.textGray}>
                        Monto promedio
                      </Typography>
                      <div className={classes.wrapRate}>
                        <Typography className={classes.bigTextBold}>
                          {customer.averageOrderAmount
                            ? commaFormat(
                                customer.averageOrderAmount,
                                currency ? currency.symbol : ''
                              )
                            : '--'}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className={classes.whiteCard}>
                  <div className={classes.cardContent}>
                    <div>
                      <Typography className={classes.textGray}>
                        Cantidad de pedidos
                      </Typography>
                      <div className={classes.wrapRate}>
                        <Typography className={classes.bigTextBold}>
                          {customer.numberOfOrders
                            ? customer.numberOfOrders
                            : 0}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.whiteCard}>
                  <div className={classes.cardContent}>
                    <div className={classes.rowContent}>
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography className={classes.textGray}>
                          Categoría de Cliente
                        </Typography>
                        <Typography className={classes.bigTextBold}>
                          {customer && customer.crmCustomerCategory
                            ? customer.crmCustomerCategory.name
                            : '--'}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.whiteCard}>
                  <div className={classes.cardContent}>
                    <div className={classes.rowContent}>
                      <Typography className={classes.titleSection}>
                        Resumen de Productos
                      </Typography>
                    </div>
                  </div>
                  {orders &&
                    orders.map((c, idx) => (
                      <CollapseItem key={`order-customer-${idx}`} order={c} />
                    ))}
                  {isFetchingOrders && (
                    <div className={classes.ordersLoader}>
                      <CircularProgress />
                    </div>
                  )}
                  {canAddPage && (
                    <CustomButton
                      className={classes.btnShowMore}
                      onClick={handleFetchMoreOrders}
                      disabled={isFetchingOrders}
                    >
                      Ver más
                    </CustomButton>
                  )}
                </div>
              </Grid>
            </Grid>
          </Hidden>
          <Hidden smDown>
            <Grid container spacing={2} className={classes.gridContent}>
              <Grid item xs={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div className={classes.whiteCard}>
                      <div className={classes.cardContent}>
                        <div className={classes.totalPurchase}>
                          <div className={classes.totalAmmount}>
                            <Typography className={classes.titleSection}>
                              Monto total de compra
                            </Typography>
                            {/* <Typography className={classes.textGray}>
                              Año: 2021
                            </Typography> */}
                          </div>
                          <Typography
                            className={classes.bigTextBold}
                            style={{fontSize: '24px'}}
                          >
                            {commaFormat(
                              customer && customer.totalOrderAmount
                                ? customer.totalOrderAmount
                                : '',
                              currency ? currency.symbol : ''
                            )}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.whiteCard}>
                      <div className={classes.cardContent}>
                        <div>
                          <Typography className={classes.textGray}>
                            Monto promedio
                          </Typography>
                          <div className={classes.wrapRate}>
                            <Typography className={classes.bigTextBold}>
                              {customer.averageOrderAmount
                                ? commaFormat(
                                    customer.averageOrderAmount,
                                    currency ? currency.symbol : ''
                                  )
                                : '--'}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.whiteCard}>
                      <div className={classes.cardContent}>
                        <div>
                          <Typography className={classes.textGray}>
                            Cantidad de pedidos
                          </Typography>
                          <div className={classes.wrapRate}>
                            <Typography className={classes.bigTextBold}>
                              {customer.numberOfOrders
                                ? customer.numberOfOrders
                                : 0}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.whiteCard}>
                      <div className={classes.cardContent}>
                        <div>
                          <Typography className={classes.textGray}>
                            Categoría de Cliente
                          </Typography>
                          <div className={classes.wrapRate}>
                            <Typography className={classes.bigTextBold}>
                              {customer && customer.crmCustomerCategory
                                ? customer.crmCustomerCategory.name
                                : '--'}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.whiteCard}>
                      <div className={classes.cardContent}>
                        <div className={classes.rowContent}>
                          <Typography className={classes.titleSection}>
                            Resumen de Productos
                          </Typography>
                        </div>
                      </div>
                      {orders &&
                        orders.map((c, idx) => (
                          <CollapseItem
                            key={`order-customer-${idx}`}
                            order={c}
                          />
                        ))}
                      {isFetchingOrders && (
                        <div className={classes.ordersLoader}>
                          <CircularProgress />
                        </div>
                      )}
                      {canAddPage && (
                        <CustomButton
                          className={classes.btnShowMore}
                          disabled={isFetchingOrders}
                          onClick={handleFetchMoreOrders}
                        >
                          Ver más
                        </CustomButton>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Grid container>
                  <Grid item xs={12}>
                    {personalInfoCustomer}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Hidden>
        </div>
      ) : null}
    </div>
  );
}

Customer.propTypes = {
  store: PropTypes.object,
  message: PropTypes.string,
  user: PropTypes.object,
  currency: PropTypes.object,
  fetchCustomer: PropTypes.func.isRequired,
  resetCustomer: PropTypes.func.isRequired,
  fetchCustomerMessage: PropTypes.func.isRequired,
  fetchCustomerOrders: PropTypes.func.isRequired,
  customer: PropTypes.object,
  orderPagination: PropTypes.object,
  isFetchingOrders: PropTypes.bool,
  orders: PropTypes.array,
  isFetching: PropTypes.bool,
};
